import { useSelector } from "react-redux";

import {
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
} from "@mui/material";

import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { LineInventoryItemHeading } from "components/formFields/line/LineInventoryItem";
import { EditLineButtonHeading } from "components/utility/EditLineButton";

import { useBins } from "hooks/useBins";
import { useClasses } from "hooks/useClasses.js";
import { useJobWorkcenters } from "hooks/useJobWorkcenters";
import { useLineItemHandlers } from "hooks/useLineItemHandlers";

import { MemoizedLineItem } from "views/Builds/Build/Output/MemoizedLineItem";

export function LineItems(props) {
  const {
    record,
    objectType,
    lines,
    items,
    location,
    itemsCount,
    addItem,
    lineHandler,
    lineItemsErrors,
  } = props;

  const { classes, classesCount } = useClasses();
  const bins = useBins(record?.location);
  const jobWorkcenters = useJobWorkcenters();

  const { readOnlyLineIds, setLineEdit } = useLineItemHandlers(
    record?.id,
    lines,
    null, // No lineHandler for this view (uses different pattern)
    null // No emptyLineItem for this view
  );

  // SETTINGS
  const classEnabled = useSelector(
    (state) => state.userCompanySettings.settings.classEnabled
  );
  const jobEnabled = useSelector(
    (state) => state.userCompanySettings.settings.jobEnabled
  );
  const uomEnabled = useSelector(
    (state) => state.userCompanySettings.settings.uomEnabled
  );
  const showQuantityOnBuildsAndShipments = useSelector(
    (state) =>
      state.userCompanySettings.settings.showQuantityOnBuildsAndShipments
  );

  if (!lines) {
    return null;
  }

  return (
    <TableContainer
      data-testing="outputLineItems"
      sx={{ overflowX: "initial" }}
    >
      <Table size="small" padding="none" stickyHeader>
        <TableHead>
          <TableRow sx={{ verticalAlign: "bottom", lineHeight: "1" }}>
            <EditLineButtonHeading recordId={record.id} />
            <LineInventoryItemHeading lineHandler={lineHandler} />
            <LineColumnHeading labelCode="Description" />
            {showQuantityOnBuildsAndShipments && (
              <LineColumnHeading labelCode="OnHand" align="right" />
            )}
            <LineColumnHeading labelCode="OnShortOrder" align="right" />
            <LineColumnHeading labelCode="QuantityToBuild" />
            {uomEnabled && <LineColumnHeading labelCode="UOM" />}
            <LineColumnHeading labelCode="AppliedToWO" />
            {classEnabled && <LineColumnHeading labelCode="Class" />}
            {jobEnabled && <LineColumnHeading labelCode="ToJob" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line, index) => {
            const lineReadOnly = readOnlyLineIds.has(index);
            return (
              <MemoizedLineItem
                key={line.id || `temp-${index}`}
                line={line}
                index={index}
                objectType={objectType}
                record={record}
                bins={bins}
                location={location}
                lineHandler={lineHandler}
                items={items}
                itemsCount={itemsCount}
                classes={classes}
                classesCount={classesCount}
                addItem={addItem}
                jobWorkcenters={jobWorkcenters}
                lineItemsErrors={lineItemsErrors}
                setLineEdit={setLineEdit}
                lineReadOnly={lineReadOnly}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
